import React, { useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import {
    Box,
    Popover,
    Typography,
    InputBase,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import { LocalizationProvider, StaticDateRangePicker } from "@mui/x-date-pickers-pro";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import {
    DATE_FILTER_OPTIONS,
    DATE_FILTER_PRESET,
    resolvePresetDates,
} from "./constants";
import "./styles.css";

const CURRENT_YEAR = moment().year();
const YEAR_OPTIONS = Array.from({ length: 41 }, (_, i) => CURRENT_YEAR + 10 - i);

// value shape: { dateType, start, end } | null
// dateType is the DATE_FILTER_PRESET integer — sent as-is to the API
// onChange receives the same shape
// options/resolveDates let callers swap in a different preset list (e.g. Lease Events'
// Notice date) while reusing the same "Choose date range" calendar behavior.
const DateFilterSelect = ({
    label,
    value,
    onChange,
    options = DATE_FILTER_OPTIONS,
    resolveDates = resolvePresetDates,
}) => {
    const [dropdownAnchor, setDropdownAnchor] = useState(null);
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const [draft, setDraft] = useState([null, null]);
    const [viewMonth, setViewMonth] = useState(() => moment().startOf("month"));
    const inputRef = useRef(null);
    const calIconRef = useRef(null);
    const calendarBodyRef = useRef(null);

    const dropdownOpen = Boolean(dropdownAnchor);
    const calendarOpen = Boolean(calendarAnchor);

    const toMoment = (iso) => (iso ? moment(iso, "YYYY-MM-DD") : null);

    const dateType = value?.dateType ?? null;
    const isCustomRange = dateType === DATE_FILTER_PRESET.CHOOSE_DATE_RANGE;

    // Reset the draft to a blank range every time the calendar opens, so the user
    // always gets a fresh start+end selection instead of editing the previous range
    // (MUI's range picker remounts on open and otherwise misreads a pre-filled range
    // as "pick the end date", closing after a single click).
    useEffect(() => {
        if (calendarOpen) {
            setDraft([null, null]);
            const start = isCustomRange ? toMoment(value?.start) : null;
            setViewMonth((start && start.isValid() ? start : moment()).clone().startOf("month"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarOpen]);

    const selectedOption = options.find((o) => o.value === dateType);

    const displayValue = () => {
        if (dateType === null || dateType === undefined) return "";
        if (isCustomRange) {
            const s = toMoment(value.start);
            const e = toMoment(value.end);
            if (s && e) return `${s.format("DD MMM YYYY")} - ${e.format("DD MMM YYYY")}`;
            return "Choose date range";
        }
        return selectedOption?.label || "";
    };

    const handleInputClick = () => {
        setDropdownAnchor(inputRef.current);
    };

    const handleDropdownClose = () => {
        setDropdownAnchor(null);
    };

    const handlePresetSelect = (preset) => {
        if (preset === DATE_FILTER_PRESET.CHOOSE_DATE_RANGE) {
            handleDropdownClose();
            setCalendarAnchor(inputRef.current);
            return;
        }
        const dates = resolveDates(preset);
        onChange({ dateType: preset, ...dates });
        handleDropdownClose();
    };

    const handleClearClick = (e) => {
        e.stopPropagation();
        setDropdownAnchor(null);
        onChange(null);
    };

    const handleCalIconClick = (e) => {
        e.stopPropagation();
        // For non-custom presets, clear start/end so calendar opens fresh
        if (dateType !== null && dateType !== undefined && !isCustomRange) {
            onChange({ dateType: DATE_FILTER_PRESET.CHOOSE_DATE_RANGE, start: null, end: null });
        }
        setCalendarAnchor(calIconRef.current || inputRef.current);
    };

    const handleCalendarClose = () => {
        setCalendarAnchor(null);
        setDraft([null, null]);
    };

    // The MUI range calendar only exposes prev/next month buttons — there is no
    // API to jump its internal "current month" state directly. To support a
    // month/year dropdown we drive those buttons programmatically, flushing each
    // click synchronously so the component's internal state advances one month
    // at a time (it recomputes the target month from its own latest state).
    const navigateToMonth = (targetMonth) => {
        const container = calendarBodyRef.current;
        if (!container) return;
        const steps = targetMonth.diff(viewMonth, "months");
        if (steps === 0) return;
        const selector = steps > 0 ? '[aria-label="Next month"]' : '[aria-label="Previous month"]';
        for (let i = 0; i < Math.abs(steps); i++) {
            const button = container.querySelector(selector);
            if (!button || button.disabled) break;
            flushSync(() => {
                button.click();
            });
        }
    };

    const handleMonthChange = (newMonth) => {
        setViewMonth(moment(newMonth).startOf("month"));
    };

    const handleMonthSelect = (e) => {
        navigateToMonth(viewMonth.clone().month(Number(e.target.value)));
    };

    const handleYearSelect = (e) => {
        navigateToMonth(viewMonth.clone().year(Number(e.target.value)));
    };

    const handleCalendarChange = ([start, end]) => {
        setDraft([start, end]);
        if (start && start.isValid() && end && end.isValid()) {
            onChange({
                dateType: DATE_FILTER_PRESET.CHOOSE_DATE_RANGE,
                start: start.format("YYYY-MM-DD"),
                end: end.format("YYYY-MM-DD"),
            });
            setCalendarAnchor(null);
        }
    };

    return (
        <Box className="dfs-root">
            {label && <Typography className="dfs-label">{label}</Typography>}

            {/* Input trigger */}
            <Box
                ref={inputRef}
                className={`dfs-input-box${dropdownOpen ? " dfs-input-box--open" : ""}`}
                onClick={handleInputClick}
            >
                <IconButton
                    ref={calIconRef}
                    size="small"
                    className="dfs-cal-icon"
                    tabIndex={-1}
                    onClick={handleCalIconClick}
                >
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                </IconButton>
                <InputBase
                    readOnly
                    value={displayValue()}
                    placeholder="Select"
                    className="dfs-input"
                    inputProps={{ style: { cursor: "pointer" } }}
                />
                {dateType !== null && dateType !== undefined && (
                    <IconButton
                        size="small"
                        className="dfs-clear-icon"
                        tabIndex={-1}
                        onClick={handleClearClick}
                    >
                        <CloseIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                    </IconButton>
                )}
                <Box className="dfs-chevron">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                            d="M2 4L6 8L10 4"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Box>
            </Box>

            {/* Preset dropdown */}
            <Popover
                open={dropdownOpen}
                anchorEl={dropdownAnchor}
                onClose={handleDropdownClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ className: "dfs-dropdown-paper" }}
                disablePortal={false}
            >
                <Paper elevation={0} className="dfs-dropdown-paper-inner">
                    <MenuList disablePadding>
                        {options.map((option) => {
                            const isSelected = dateType === option.value;
                            return (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handlePresetSelect(option.value)}
                                    className={`dfs-menu-item${isSelected ? " dfs-menu-item--selected" : ""}`}
                                >
                                    <span className="dfs-menu-item-label">{option.label}</span>
                                    {isSelected && (
                                        <CheckIcon className="dfs-menu-item-check" />
                                    )}
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </Paper>
            </Popover>

            {/* Calendar popover — only for custom date range */}
            <Popover
                open={calendarOpen}
                anchorEl={calendarAnchor}
                onClose={handleCalendarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ className: "dfs-calendar-paper" }}
                disablePortal={false}
                keepMounted={false}
            >
                <Box className="dfs-calendar-header">
                    <IconButton
                        size="small"
                        onClick={() => {
                            handleCalendarClose();
                            setDropdownAnchor(inputRef.current);
                        }}
                        className="dfs-calendar-back"
                    >
                        <ArrowBackIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography className="dfs-calendar-title">Choose date range</Typography>
                </Box>
                <Box className="dfs-calendar-monthyear">
                    <select
                        className="dfs-calendar-select"
                        aria-label="Select month"
                        value={viewMonth.month()}
                        onChange={handleMonthSelect}
                    >
                        {moment.months().map((name, idx) => (
                            <option key={name} value={idx}>{name}</option>
                        ))}
                    </select>
                    <select
                        className="dfs-calendar-select"
                        aria-label="Select year"
                        value={viewMonth.year()}
                        onChange={handleYearSelect}
                    >
                        {YEAR_OPTIONS.map((yr) => (
                            <option key={yr} value={yr}>{yr}</option>
                        ))}
                    </select>
                </Box>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Box ref={calendarBodyRef}>
                        <StaticDateRangePicker
                            value={draft}
                            onChange={handleCalendarChange}
                            onMonthChange={handleMonthChange}
                            calendars={1}
                            slotProps={{
                                actionBar: { actions: [] },
                                toolbar: { hidden: true },
                            }}
                            sx={{
                                "& .MuiDateRangePickerDay-rangeIntervalDayHighlight": {
                                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                                },
                                "& .MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: "#1976d2",
                                    color: "#fff",
                                    "&:hover": { backgroundColor: "#1565c0" },
                                },
                                "& .MuiDateRangePickerDay-day.Mui-selected": {
                                    backgroundColor: "#1976d2",
                                    color: "#fff",
                                },
                                "& .MuiPickersDay-root:hover": {
                                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                                },
                                fontFamily: "'Source Sans Pro' !important",
                            }}
                        />
                    </Box>
                </LocalizationProvider>
            </Popover>
        </Box>
    );
};

export default DateFilterSelect;
